'use client';

import { ExternalLink } from 'lucide-react';
import { useCallback, type ComponentType, type ReactNode } from 'react';
import { CopyButtonBase, useCopyFeedback } from '@/components/ui/copy-button';
import { CitationLink, parseCitationUrl } from './CitationLink';
import { LegalTextBlock, type CitationClickHandler } from './legal-text-block';
import { Mermaid } from '@/components/ui/mermaid';

/**
 * Minimal component map for the markdown elements rendered here.
 *
 * Deliberately avoids react-markdown's `Components` type, which is a mapped
 * type over all ~170 HTML elements and is the dominant TypeScript cost of
 * this module. Each renderer only needs `children` plus the specific props it
 * destructures, so the narrow map is assignable to `Components` at the
 * ReactMarkdown call site.
 */
export interface MarkdownComponents {
  h1?: ComponentType<{ children?: ReactNode }>;
  h2?: ComponentType<{ children?: ReactNode }>;
  h3?: ComponentType<{ children?: ReactNode }>;
  p?: ComponentType<{ children?: ReactNode }>;
  ul?: ComponentType<{ children?: ReactNode }>;
  ol?: ComponentType<{ children?: ReactNode }>;
  li?: ComponentType<{ children?: ReactNode }>;
  strong?: ComponentType<{ children?: ReactNode }>;
  a?: ComponentType<{ children?: ReactNode; href?: string; target?: string; rel?: string }>;
  table?: ComponentType<{ children?: ReactNode }>;
  thead?: ComponentType<{ children?: ReactNode }>;
  tbody?: ComponentType<{ children?: ReactNode }>;
  tr?: ComponentType<{ children?: ReactNode }>;
  th?: ComponentType<{ children?: ReactNode }>;
  td?: ComponentType<{ children?: ReactNode }>;
  code?: ComponentType<{ children?: ReactNode; className?: string }>;
  pre?: ComponentType<{ children?: ReactNode }>;
}

export type WebSourceClickHandler = (url: string, title?: string) => void;

/** Options used to build the markdown element renderers. */
interface MarkdownComponentsOptions {
  onCitationClick?: CitationClickHandler;
  enableCopy: boolean;
  isStreaming: boolean;
  /** Opens the mermaid diagram modal; owned by the renderer's modal state. */
  handleMermaidClick: (chart: string) => void;
  customComponents?: Partial<MarkdownComponents>;
}

/**
 * Builds the element renderers for ReactMarkdown.
 *
 * Kept as a standalone factory (not inline in the renderer) so the renderer
 * can memoize the result and so the destructured `children`/`className`
 * props of each renderer do not shadow the renderer's own props.
 */
export function createMarkdownComponents(options: MarkdownComponentsOptions): Partial<MarkdownComponents> {
  const { onCitationClick, enableCopy, isStreaming, handleMermaidClick, customComponents } = options;

  return {
    h1: ({ children }) => (
      <h1 className="text-xl font-bold text-neutral-900 mb-4 mt-6">{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-lg font-semibold text-neutral-900 mb-3 mt-5">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-base font-semibold text-neutral-900 mb-2 mt-4">{children}</h3>
    ),
    p: ({ children }) => (
      <p className="mb-4 leading-[1.7] text-neutral-900 last:mb-0 text-justify">{children}</p>
    ),
    ul: ({ children }) => <ul className="list-disc pl-8 mb-5 space-y-4">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal pl-8 mb-5 space-y-4">{children}</ol>,
    li: ({ children }) => (
      <li className="pl-1 text-neutral-900 leading-[1.7] marker:text-neutral-900 text-justify">
        {children}
      </li>
    ),
    strong: ({ children }) => <span className="font-semibold text-neutral-900">{children}</span>,
    a: ({ children, href, ...props }) => {
      if (href && href.startsWith('ext:')) {
        const rawUrl = href.slice(4).trim();
        const url = rawUrl || href;
        // ext: links are web sources — always open in new tab. Stripping the
        // prefix prevents a blank navigation to the non-standard ext: scheme.
        return (
          <a
            href={url}
            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 transition-colors underline underline-offset-2"
            target="_blank"
            rel="noopener noreferrer"
            {...props}
          >
            <span>{children}</span>
            <ExternalLink size={12} className="shrink-0" />
          </a>
        );
      }
      if (href && (href.startsWith('regulation:') || href.startsWith('court_case:'))) {
        const parsed = parseCitationUrl(href);
        if (parsed) {
          if (onCitationClick) {
            return (
              <CitationLink
                regulationId={parsed.regulationId}
                caseLawCourtDecisionId={parsed.caseLawCourtDecisionId}
                articleNumber={parsed.articleNumber}
                verseNumber={parsed.verseNumber}
                onCitationClick={onCitationClick}
              >
                {children}
              </CitationLink>
            );
          }
          return <span className="text-neutral-600 cursor-default">{children}</span>;
        }
      }
      return (
        <a
          href={href}
          className="inline-flex items-center gap-1 text-neutral-600 hover:text-neutral-900 transition-colors"
          target="_blank"
          rel="noopener noreferrer"
          {...props}
        >
          <ExternalLink size={14} className="shrink-0" />
          <span>{children}</span>
        </a>
      );
    },
    table: ({ children }) => {
      if (!enableCopy) {
        return (
          <div className="overflow-x-auto my-4">
            <table className="min-w-full border-collapse border border-neutral-200 text-sm">
              {children}
            </table>
          </div>
        );
      }
      return <CopyableTable>{children}</CopyableTable>;
    },
    thead: ({ children }) => <thead className="bg-neutral-50">{children}</thead>,
    tbody: ({ children }) => <tbody>{children}</tbody>,
    tr: ({ children }) => <tr className="border-b border-neutral-200">{children}</tr>,
    th: ({ children }) => (
      <th className="border border-neutral-200 px-4 py-2 text-left font-semibold text-neutral-900">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="border border-neutral-200 px-4 py-2 text-neutral-700">{children}</td>
    ),
    code: ({ className, children, ...props }) => {
      const isInline = !className;

      if (isInline) {
        return (
          <code
            className="bg-neutral-100 text-neutral-800 px-1.5 py-0.5 rounded text-sm font-mono"
            {...props}
          >
            {children}
          </code>
        );
      }

      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';
      const codeContent = String(children).replace(/\n$/, '');

      if (language === 'mermaid') {
        return (
          <Mermaid
            chart={codeContent}
            onChartClick={enableCopy ? handleMermaidClick : undefined}
            enableCopy={enableCopy}
            isStreaming={isStreaming}
          />
        );
      }

      if (language === 'legaltext') {
        return <LegalTextBlock content={codeContent} onCitationClick={onCitationClick} />;
      }

      return (
        <pre className="bg-neutral-100 text-neutral-800 p-4 rounded-lg overflow-x-auto my-4 text-sm font-mono">
          <code className={className}>{children}</code>
        </pre>
      );
    },
    pre: ({ children }) => <>{children}</>,
    ...customComponents,
  };
}

/** Flattens a rendered HTML table into copyable plain text with a header row. */
function htmlToPlainTextTable(html: string): string {
  let text = html;
  text = text.replace(/<thead[^>]*>/gi, '');
  text = text.replace(/<\/thead>/gi, '');
  text = text.replace(/<tbody[^>]*>/gi, '');
  text = text.replace(/<\/tbody>/gi, '');
  text = text.replace(/<tr[^>]*>/gi, '');
  text = text.replace(/<\/tr>/gi, '\n');
  text = text.replace(/<th[^>]*>/gi, '| ');
  text = text.replace(/<\/th>/gi, ' ');
  text = text.replace(/<td[^>]*>/gi, '| ');
  text = text.replace(/<\/td>/gi, ' ');
  text = text.replace(/<[^>]+>/g, '');
  text = text.replace(/&nbsp;/g, ' ');
  text = text.replace(/&amp;/g, '&');
  text = text.replace(/&lt;/g, '<');
  text = text.replace(/&gt;/g, '>');
  text = text.replace(/\n\s*\n/g, '\n');
  text = text.trim();
  const lines = text.split('\n').filter((line) => line.trim());
  if (lines.length > 0) {
    const firstLine = lines[0];
    const colCount = (firstLine.match(/\|/g) || []).length;
    if (colCount > 0) {
      const separator = '|' + '----------|'.repeat(colCount);
      lines.splice(1, 0, separator);
    }
  }
  return lines.join('\n');
}

/** Table wrapper with a copy button that copies styled HTML and plain text. */
function CopyableTable({ children }: { children: ReactNode }) {
  const { copied, isCopying, setIsCopying, markCopied } = useCopyFeedback();

  const handleCopy = useCallback(async () => {
    setIsCopying(true);
    try {
      const tableElement = document.activeElement
        ?.closest('div[data-table-wrapper]')
        ?.querySelector('table');
      if (!tableElement) return;

      const html = tableElement.outerHTML;
      const plainText = htmlToPlainTextTable(html);

      const htmlWithStyles = html
        .replace(
          /<table/,
          '<table style="border-collapse: collapse; width: 100%; font-family: Calibri, Arial, sans-serif;"',
        )
        .replace(
          /<th/g,
          '<th style="border: 1px solid #d4d4d4; padding: 8px; background-color: #f5f5f5; font-weight: 600; text-align: left;"',
        )
        .replace(/<td/g, '<td style="border: 1px solid #d4d4d4; padding: 8px; text-align: left;"')
        .replace(/<tr/g, '<tr style="border-bottom: 1px solid #e5e5e5;"');

      const htmlBlob = new Blob([htmlWithStyles], { type: 'text/html' });
      const textBlob = new Blob([plainText], { type: 'text/plain' });

      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': htmlBlob,
          'text/plain': textBlob,
        }),
      ]);

      markCopied();
    } catch (error) {
      console.error('Failed to copy table:', error);
    } finally {
      setIsCopying(false);
    }
  }, [markCopied, setIsCopying]);

  return (
    <div data-table-wrapper className="group relative overflow-x-auto my-4">
      <table className="min-w-full border-collapse border border-neutral-200 text-sm">
        {children}
      </table>
      <CopyButtonBase
        onCopy={handleCopy}
        isCopying={isCopying}
        copied={copied}
        variant="floating"
        label="Salin Tabel"
        className="absolute top-1 right-1"
      />
    </div>
  );
}
